import { useState } from 'react'
import type { CollectionItem } from '../lib/types'
import { useCreatorStore } from '../stores/creatorStore'

const TYPE_ICONS: Record<string, string> = {
  exchange: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-11.25 4.5l-2.25 2.25V6.75A2.25 2.25 0 015.625 4.5h12.75a2.25 2.25 0 012.25 2.25v7.5a2.25 2.25 0 01-2.25 2.25H8.625z',
  youtube: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z',
  blog: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582',
  pdf: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  notecard: 'M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z',
}

interface Props {
  item: CollectionItem
  collectionId: number
  index: number
  total: number
  onMoveUp: () => void
  onMoveDown: () => void
}

export default function CollectionItemCard({ item, collectionId, index, total, onMoveUp, onMoveDown }: Props) {
  const { removeItem, updateItemNote } = useCreatorStore()
  const [note, setNote] = useState(item.note || '')

  const entryType = item.timeline_entry_type || 'exchange'
  const iconPath = TYPE_ICONS[entryType] || TYPE_ICONS.exchange

  return (
    <div className="bg-surface rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        {/* Type icon */}
        <div className="shrink-0 mt-0.5 text-text-muted">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-secondary truncate">
            {item.thread_title || 'Thread'}
          </p>
          <p className="text-sm text-text-muted line-clamp-2 mt-0.5">
            {item.timeline_entry_preview || `Entry #${item.timeline_entry}`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-0.5 shrink-0">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 rounded hover:bg-elevated text-text-muted hover:text-text-primary disabled:opacity-20 transition-colors"
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1 rounded hover:bg-elevated text-text-muted hover:text-text-primary disabled:opacity-20 transition-colors"
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
        </div>

        <button
          onClick={() => removeItem(collectionId, item.id)}
          className="p-1.5 rounded hover:bg-bg text-text-muted hover:text-error transition-colors shrink-0"
          title="Remove"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Note field */}
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if (note !== (item.note || '')) updateItemNote(collectionId, item.id, note) }}
        placeholder="Add a note..."
        className="w-full text-xs bg-elevated text-text-primary placeholder-text-muted rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  )
}
